import { useState, useEffect, useRef } from 'react';
import { X, Upload, RefreshCw, Save, Eye } from 'lucide-react';
import { toast } from 'sonner';
import ModalOverlay from 'components/ModalOverlay';

/* ─── DiplomaConfigModal ─────────────────────────────────────────────────────
   Props:
   - open: boolean
   - onClose: fn
   - moduleId: string
   - moduleTitle: string
   - initialConfig: object | null  (diplomaConfig guardada en el módulo)
   - api: axios instance
   - onSaved: fn()  → callback para refrescar el panel padre
───────────────────────────────────────────────────────────────────────────── */

const FONTS = [
  { label: 'Playfair', value: "'Playfair Display', serif" },
  { label: 'Georgia',  value: "'Georgia', serif" },
  { label: 'DM Sans',  value: "'DM Sans', sans-serif" },
  { label: 'Courier',  value: "'Courier New', monospace" },
];

const GRAD_DIRS = [
  { label: 'Diagonal', value: '135deg' },
  { label: 'Vertical', value: '0deg' },
  { label: 'Horizontal', value: '90deg' },
];

const DEFAULT_CONFIG = {
  bgMode: 'color',
  bgColor: '#faf7f0',
  gradColor1: '#faf7f0',
  gradColor2: '#e8e0d0',
  gradDir: '135deg',
  bgImage: null,
  textColor: '#2a1f0e',
  accentColor: '#b8975a',
  fontFamily: "'Playfair Display', serif",
  orgName: 'Plataforma de Formación',
  headline: 'Certificate of Completion',
  certText: 'Certifica que',
  completedText: 'ha completado satisfactoriamente el módulo',
  signerName: 'Dirección Académica',
  signerLabel: 'Firma autorizada',
  sealText: 'CERT✦VALID',
  sealImage: null,
};

/* ── Styles (scoped inside modal) ── */
const MODAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@400;500;700&display=swap');

  .dcm-overlay {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(0,0,0,0.85);
    backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    padding: 16px;
  }
  .dcm-modal {
    background: #080614;
    border: 1px solid rgba(0,255,255,0.18);
    border-radius: 22px;
    width: 100%; max-width: 860px; max-height: 92vh;
    display: flex; flex-direction: column;
    box-shadow: 0 0 60px rgba(0,255,255,0.1);
    overflow: hidden;
  }
  .dcm-header {
    padding: 20px 26px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    background: linear-gradient(to right, rgba(0,255,255,0.06), transparent);
    display: flex; align-items: center; justify-content: space-between;
    flex-shrink: 0;
  }
  .dcm-body {
    display: grid;
    grid-template-columns: 260px 1fr;
    flex: 1;
    overflow: hidden;
  }
  .dcm-controls {
    padding: 16px;
    border-right: 1px solid rgba(255,255,255,0.06);
    overflow-y: auto;
    display: flex; flex-direction: column; gap: 0;
  }
  .dcm-controls::-webkit-scrollbar { width: 3px; }
  .dcm-controls::-webkit-scrollbar-thumb { background: rgba(0,255,255,0.2); border-radius: 3px; }
  .dcm-preview-pane {
    padding: 20px;
    background: rgba(0,0,0,0.3);
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    overflow-y: auto;
  }
  .dcm-footer {
    padding: 14px 24px;
    border-top: 1px solid rgba(255,255,255,0.06);
    background: rgba(0,0,0,0.3);
    display: flex; justify-content: flex-end; gap: 10px;
    flex-shrink: 0;
  }

  /* Controls */
  .dcm-section {
    font-size: 9px; font-weight: 800; letter-spacing: 0.18em;
    text-transform: uppercase; color: rgba(0,255,255,0.6);
    margin: 14px 0 8px;
  }
  .dcm-section:first-child { margin-top: 0; }
  .dcm-row { display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px; }
  .dcm-label { font-size: 11px; color: rgba(255,255,255,0.5); font-family: 'DM Sans', sans-serif; }
  .dcm-input {
    width: 100%; padding: 8px 10px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; color: #fff; font-size: 12px;
    font-family: 'DM Sans', sans-serif;
    transition: border-color 0.2s;
    box-sizing: border-box;
  }
  .dcm-input:focus { outline: none; border-color: #00FFFF; }
  .dcm-input::placeholder { color: rgba(255,255,255,0.2); }
  .dcm-color-row { display: flex; align-items: center; gap: 8px; }
  .dcm-color-input {
    width: 36px; height: 32px; padding: 2px 3px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 8px; cursor: pointer; flex-shrink: 0;
    background: rgba(255,255,255,0.04);
  }
  .dcm-modes { display: grid; grid-template-columns: repeat(3,1fr); gap: 5px; margin-bottom: 8px; }
  .dcm-mode-btn {
    padding: 6px 4px; font-size: 10px; font-weight: 700;
    text-align: center; border-radius: 8px; cursor: pointer;
    border: 1px solid rgba(255,255,255,0.08);
    background: transparent; color: rgba(255,255,255,0.4);
    font-family: 'DM Sans', sans-serif; transition: all 0.2s;
  }
  .dcm-mode-btn.active { background: rgba(0,255,255,0.1); border-color: rgba(0,255,255,0.35); color: #00FFFF; }
  .dcm-fonts { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; }
  .dcm-font-btn {
    padding: 6px; text-align: center; border-radius: 8px; cursor: pointer;
    border: 1px solid rgba(255,255,255,0.08);
    background: transparent; color: rgba(255,255,255,0.5);
    font-size: 12px; transition: all 0.2s;
  }
  .dcm-font-btn.active { border-color: rgba(0,255,255,0.4); color: #00FFFF; background: rgba(0,255,255,0.06); }
  .dcm-upload-btn {
    width: 100%; display: flex; align-items: center; gap: 7px;
    padding: 8px 10px;
    background: rgba(255,255,255,0.03);
    border: 1px dashed rgba(255,255,255,0.12);
    border-radius: 9px; cursor: pointer; color: rgba(255,255,255,0.5);
    font-size: 11px; font-family: 'DM Sans', sans-serif;
    transition: all 0.2s; margin-bottom: 6px;
  }
  .dcm-upload-btn:hover { background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.8); }
  .dcm-file-name { font-size: 10px; color: #00FFFF; margin-bottom: 6px; }
  .dcm-hidden { display: none; }
  .dcm-select {
    width: 100%; padding: 7px 10px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; color: #fff; font-size: 12px;
    font-family: 'DM Sans', sans-serif;
    box-sizing: border-box;
  }
  .dcm-select option { background: #060810; }

  /* Buttons */
  .dcm-btn-primary {
    display: flex; align-items: center; gap: 6px;
    background: #00FFFF; color: #000; font-weight: 800;
    border: none; border-radius: 10px; padding: 10px 22px;
    font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
    cursor: pointer; font-family: 'DM Sans', sans-serif;
    box-shadow: 0 0 14px rgba(0,255,255,0.3);
    transition: all 0.2s;
  }
  .dcm-btn-primary:hover { background: #66FFFF; }
  .dcm-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .dcm-btn-ghost {
    display: flex; align-items: center; gap: 6px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.6); border-radius: 10px;
    padding: 9px 16px; font-size: 11px; letter-spacing: 0.08em;
    text-transform: uppercase; cursor: pointer;
    font-family: 'DM Sans', sans-serif; transition: all 0.2s;
  }
  .dcm-btn-ghost:hover { background: rgba(255,255,255,0.1); color: #fff; }

  /* Live diploma preview (mini) */
  .dcm-diploma-preview {
    width: 100%; max-width: 500px;
    aspect-ratio: 1.41 / 1;
    position: relative;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0,0,0,0.5);
    flex-shrink: 0;
  }
  .dcm-cert-inner {
    width: 100%; height: 100%;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 20px 28px;
    position: relative; gap: 0;
  }
  .dcm-corner {
    position: absolute; width: 36px; height: 36px;
    border: 2.5px solid var(--p-accent);
  }
  .dcm-corner.tl { top: 10px; left: 10px; border-right: none; border-bottom: none; }
  .dcm-corner.br { bottom: 10px; right: 10px; border-left: none; border-top: none; }
  .dcm-frame {
    position: absolute;
    inset: 18px;
    border: 1px solid var(--p-accent);
    opacity: 0.7;
    pointer-events: none;
  }
  .dcm-p-org { font-size: 7px; font-weight: 700; letter-spacing: 0.25em; text-transform: uppercase; color: var(--p-accent); margin-bottom: 2px; }
  .dcm-p-headline { font-size: 7px; font-style: italic; color: var(--p-text); opacity: 0.6; margin-bottom: 8px; letter-spacing: 0.1em; }
  .dcm-p-divider { width: 120px; height: 1px; background: linear-gradient(to right, transparent, var(--p-accent), transparent); margin: 5px auto; }
  .dcm-p-certifies { font-size: 6px; letter-spacing: 0.15em; text-transform: uppercase; color: var(--p-text); opacity: 0.6; margin-bottom: 4px; }
  .dcm-p-name { font-size: 22px; font-weight: 700; color: var(--p-text); line-height: 1.1; margin-bottom: 3px; }
  .dcm-p-completed { font-size: 7px; color: var(--p-text); opacity: 0.6; margin-bottom: 8px; }
  .dcm-p-module { font-size: 11px; font-weight: 700; font-style: italic; color: var(--p-accent); text-align: center; padding: 0 10px; }
  .dcm-p-footer {
    display: flex; justify-content: space-between; align-items: flex-end;
    width: 100%; margin-top: auto; padding-top: 8px;
    border-top: 1px solid var(--p-accent);
  }
  .dcm-p-seal {
    width: 40px; height: 40px; border-radius: 50%;
    border: 1.5px solid var(--p-accent);
    display: flex; align-items: center; justify-content: center;
    font-size: 5px; font-weight: 800; text-align: center;
    color: var(--p-accent); text-transform: uppercase; line-height: 1.3;
    overflow: hidden; flex-shrink: 0;
  }
  .dcm-p-seal img { width: 100%; height: 100%; object-fit: contain; padding: 3px; }
  .dcm-p-sig { text-align: center; flex: 1; padding: 0 8px; }
  .dcm-p-sig-name { font-size: 10px; font-style: italic; color: var(--p-text); }
  .dcm-p-sig-line { width: 70px; height: 1px; background: var(--p-accent); opacity: 0.4; margin: 2px auto; }
  .dcm-p-sig-label { font-size: 5px; letter-spacing: 0.15em; text-transform: uppercase; color: var(--p-text); opacity: 0.5; }
  .dcm-p-date { text-align: right; font-size: 7px; color: var(--p-text); opacity: 0.6; }
  .dcm-preview-label {
    font-size: 10px; font-weight: 700; letter-spacing: 0.12em;
    text-transform: uppercase; color: rgba(0,255,255,0.5);
    display: flex; align-items: center; gap: 5px;
  }
`;

/* ── Helper: build background string ── */
const buildBg = (cfg) => {
  if (cfg.bgMode === 'image' && cfg.bgImage) return `url(${cfg.bgImage}) center/cover no-repeat`;
  if (cfg.bgMode === 'gradient') return `linear-gradient(${cfg.gradDir}, ${cfg.gradColor1}, ${cfg.gradColor2})`;
  return cfg.bgColor || '#faf7f0';
};

/* ══════════════════════════════════════════════════════════════════════════ */
const DiplomaConfigModal = ({
  open, onClose,
  scope = 'module',          // 'module' | 'organization'
  moduleId, organizationId,
  moduleTitle, orgName,
  initialConfig, api, onSaved,
}) => {
  const [cfg, setCfg] = useState({ ...DEFAULT_CONFIG, ...(initialConfig || {}) });
  const [saving, setSaving] = useState(false);
  const bgImgRef = useRef();
  const sealImgRef = useRef();

  useEffect(() => {
    if (open) setCfg({ ...DEFAULT_CONFIG, ...(initialConfig || {}) });
  }, [open, initialConfig]);

  if (!open) return null;

  const isOrgScope = scope === 'organization';
  const targetId = isOrgScope ? organizationId : moduleId;

  const configEndpoint = isOrgScope
    ? `/admin/organizations/${targetId}/diploma-config`
    : `/admin/modules/${targetId}/diploma-config`;

  const imageEndpoint = isOrgScope
    ? `/admin/organizations/${targetId}/diploma-image`
    : `/admin/modules/${targetId}/diploma-image`;

  const set = (key, val) => setCfg(p => ({ ...p, [key]: val }));

  // Ahora sube la imagen al backend (antes solo la metía en base64 local)
  const loadImage = async (e, key, labelId) => {
    const file = e.target.files[0];
    if (!file) return;

    const label = document.getElementById(labelId);
    if (label) label.textContent = 'Subiendo...';

    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(
        `${imageEndpoint}?field=${key}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      set(key, data.url);
      if (label) label.textContent = '✓ ' + file.name;
    } catch (err) {
      toast.error('No se pudo subir la imagen');
      if (label) label.textContent = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(configEndpoint, cfg);
      toast.success(
        isOrgScope
          ? 'Diploma corporativo guardado. Se usará por defecto en todos los módulos que no tengan uno propio.'
          : 'Configuración del diploma guardada para este módulo.'
      );
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setCfg({ ...DEFAULT_CONFIG });

  const previewVars = {
    '--p-text': cfg.textColor,
    '--p-accent': cfg.accentColor,
    background: buildBg(cfg),
    fontFamily: cfg.fontFamily,
  };

  const headerSubtitle = isOrgScope ? (orgName || 'Diploma corporativo') : moduleTitle;

   return (
    <>
      <style>{MODAL_STYLES}</style>
      <ModalOverlay className="dcm-overlay" label={isOrgScope ? 'Diploma corporativo' : 'Personalizar diploma'} onClose={onClose}>
  <div className="dcm-modal">

          {/* Header */}
          <div className="dcm-header">
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>
                {isOrgScope ? 'Diploma corporativo' : 'Personalizar Diploma'}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 3 }}>
                {isOrgScope ? (orgName || 'Organización') : moduleTitle}
              </div>
              {isOrgScope && (
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                  Se aplicará por defecto a todos los módulos sin diseño propio
                </div>
              )}
            </div>
            <button className="dcm-btn-ghost" onClick={onClose} style={{ padding: '8px' }}>
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="dcm-body">

            {/* ── Left: Controls ── */}
            <div className="dcm-controls">

              {/* Fondo */}
              <div className="dcm-section">Fondo</div>
              <div className="dcm-modes">
                {[
                  { id: 'color', label: 'Color' },
                  { id: 'gradient', label: 'Degradado' },
                  { id: 'image', label: 'Imagen' },
                ].map(m => (
                  <button key={m.id} className={`dcm-mode-btn${cfg.bgMode === m.id ? ' active' : ''}`}
                    onClick={() => set('bgMode', m.id)}>
                    {m.label}
                  </button>
                ))}
              </div>

              {cfg.bgMode === 'color' && (
                <div className="dcm-row">
                  <div className="dcm-label">Color de fondo</div>
                  <div className="dcm-color-row">
                    <input type="color" className="dcm-color-input" value={cfg.bgColor}
                      onChange={e => set('bgColor', e.target.value)} />
                    <input className="dcm-input" value={cfg.bgColor}
                      onChange={e => set('bgColor', e.target.value)} style={{ flex: 1 }} />
                  </div>
                </div>
              )}

              {cfg.bgMode === 'gradient' && (
                <>
                  <div className="dcm-row">
                    <div className="dcm-label">Color 1</div>
                    <div className="dcm-color-row">
                      <input type="color" className="dcm-color-input" value={cfg.gradColor1}
                        onChange={e => set('gradColor1', e.target.value)} />
                      <input className="dcm-input" value={cfg.gradColor1}
                        onChange={e => set('gradColor1', e.target.value)} style={{ flex: 1 }} />
                    </div>
                  </div>
                  <div className="dcm-row">
                    <div className="dcm-label">Color 2</div>
                    <div className="dcm-color-row">
                      <input type="color" className="dcm-color-input" value={cfg.gradColor2}
                        onChange={e => set('gradColor2', e.target.value)} />
                      <input className="dcm-input" value={cfg.gradColor2}
                        onChange={e => set('gradColor2', e.target.value)} style={{ flex: 1 }} />
                    </div>
                  </div>
                  <div className="dcm-row">
                    <div className="dcm-label">Dirección</div>
                    <select className="dcm-select" value={cfg.gradDir} onChange={e => set('gradDir', e.target.value)}>
                      {GRAD_DIRS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                </>
              )}

              {cfg.bgMode === 'image' && (
                <div>
                  <input type="file" accept="image/*" ref={bgImgRef} className="dcm-hidden"
                    onChange={e => loadImage(e, 'bgImage', 'bgImgName')} />
                  <button className="dcm-upload-btn" onClick={() => bgImgRef.current.click()}>
                    <Upload size={13} /> Subir imagen de fondo
                  </button>
                  <div id="bgImgName" className="dcm-file-name">
                    {cfg.bgImage ? '✓ Imagen cargada' : ''}
                  </div>
                </div>
              )}

              {/* Colores */}
              <div className="dcm-section">Colores</div>
              <div className="dcm-row">
                <div className="dcm-label">Color del texto</div>
                <div className="dcm-color-row">
                  <input type="color" className="dcm-color-input" value={cfg.textColor}
                    onChange={e => set('textColor', e.target.value)} />
                  <input className="dcm-input" value={cfg.textColor}
                    onChange={e => set('textColor', e.target.value)} style={{ flex: 1 }} />
                </div>
              </div>
              <div className="dcm-row">
                <div className="dcm-label">Color de acento</div>
                <div className="dcm-color-row">
                  <input type="color" className="dcm-color-input" value={cfg.accentColor}
                    onChange={e => set('accentColor', e.target.value)} />
                  <input className="dcm-input" value={cfg.accentColor}
                    onChange={e => set('accentColor', e.target.value)} style={{ flex: 1 }} />
                </div>
              </div>

              {/* Fuente */}
              <div className="dcm-section">Tipografía</div>
              <div className="dcm-fonts">
                {FONTS.map(f => (
                  <button key={f.value}
                    className={`dcm-font-btn${cfg.fontFamily === f.value ? ' active' : ''}`}
                    style={{ fontFamily: f.value }}
                    onClick={() => set('fontFamily', f.value)}>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Textos */}
              <div className="dcm-section">Textos</div>
              {[
                { key: 'orgName',        label: 'Organización' },
                { key: 'headline',       label: 'Subtítulo' },
                { key: 'certText',       label: 'Texto de certificación' },
                { key: 'completedText',  label: 'Texto de completado' },
                { key: 'signerName',     label: 'Nombre del firmante' },
                { key: 'signerLabel',    label: 'Cargo del firmante' },
              ].map(f => (
                <div className="dcm-row" key={f.key}>
                  <div className="dcm-label">{f.label}</div>
                  <input className="dcm-input" value={cfg[f.key]}
                    onChange={e => set(f.key, e.target.value)} />
                </div>
              ))}

              {/* Sello */}
              <div className="dcm-section">Sello</div>
              <input type="file" accept="image/*" ref={sealImgRef} className="dcm-hidden"
                onChange={e => loadImage(e, 'sealImage', 'sealImgName')} />
              <button className="dcm-upload-btn" onClick={() => sealImgRef.current.click()}>
                <Upload size={13} /> Subir imagen del sello
              </button>
              {cfg.sealImage && (
                <button className="dcm-upload-btn" style={{ borderColor: 'rgba(239,68,68,0.3)', color: '#f87171' }}
                  onClick={() => { set('sealImage', null); document.getElementById('sealImgName').textContent = ''; }}>
                  <X size={12} /> Quitar imagen del sello
                </button>
              )}
              <div id="sealImgName" className="dcm-file-name">
                {cfg.sealImage ? '✓ Imagen cargada' : ''}
              </div>
              <div className="dcm-row">
                <div className="dcm-label">Texto del sello (si no hay imagen)</div>
                <input className="dcm-input" value={cfg.sealText}
                  onChange={e => set('sealText', e.target.value)} />
              </div>

            </div>

            {/* ── Right: Preview ── */}
            <div className="dcm-preview-pane">
              <div className="dcm-preview-label">
                <Eye size={13} /> Vista previa en tiempo real
              </div>

              {/* Mini diploma preview */}
              <div className="dcm-diploma-preview" style={previewVars}>
                <div className="dcm-cert-inner">
                  <div className="dcm-corner tl" />
                  <div className="dcm-corner br" />
                  <div className="dcm-frame" />

                  <div className="dcm-p-org">{cfg.orgName}</div>
                  <div className="dcm-p-headline" style={{ fontFamily: cfg.fontFamily }}>{cfg.headline}</div>
                  <div className="dcm-p-divider" />
                  <div className="dcm-p-certifies">{cfg.certText}</div>
                  <div className="dcm-p-name" style={{ fontFamily: cfg.fontFamily }}>Nombre Empleado</div>
                  <div className="dcm-p-completed">{cfg.completedText}</div>
                  <div className="dcm-p-module" style={{ fontFamily: cfg.fontFamily }}>
                    {isOrgScope
                      ? 'Título del módulo (ejemplo)'
                      : (moduleTitle || 'Título del Módulo')}
                  </div>
                  <div className="dcm-p-divider" style={{ marginTop: 6 }} />

                  <div className="dcm-p-footer">
                    <div className="dcm-p-seal">
                      {cfg.sealImage
                        ? <img src={cfg.sealImage} alt="Sello" />
                        : <span style={{ whiteSpace: 'pre-line', padding: 3 }}>
                            {(cfg.sealText || 'CERT✦VALID').replaceAll('✦', '\n✦\n')}
                          </span>
                      }
                    </div>
                    <div className="dcm-p-sig">
                      <div className="dcm-p-sig-name" style={{ fontFamily: cfg.fontFamily }}>{cfg.signerName}</div>
                      <div className="dcm-p-sig-line" />
                      <div className="dcm-p-sig-label">{cfg.signerLabel}</div>
                    </div>
                    <div className="dcm-p-date">
                      <div style={{ fontSize: 5, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: .5, marginBottom: 2 }}>Fecha de emisión</div>
                      {new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Color swatches summary */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                {[
                  { label: 'Texto', color: cfg.textColor },
                  { label: 'Acento', color: cfg.accentColor },
                ].map(s => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                    <div style={{ width: 14, height: 14, borderRadius: 4, background: s.color, border: '1px solid rgba(255,255,255,0.2)' }} />
                    {s.label}: {s.color}
                  </div>
                ))}
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                  {FONTS.find(f => f.value === cfg.fontFamily)?.label || 'Fuente'}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="dcm-footer">
            <button className="dcm-btn-ghost" onClick={reset}>
              <RefreshCw size={13} /> Restablecer
            </button>
            <button className="dcm-btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="dcm-btn-primary" onClick={handleSave} disabled={saving}>
              <Save size={13} /> {saving ? 'Guardando...' : (isOrgScope ? 'Guardar diploma corporativo' : 'Guardar diploma')}
            </button>
          </div>

        </div>
      </ModalOverlay>
    </>
  );
};

export default DiplomaConfigModal;